import { ProfileFieldSlot, ProfileFieldLabel } from '@/components/admin/PersonFields';
/* Who the person is: name, contact, status in Canada, and where they live.

   Rendered identically by the admissions page and the student page. Both feed
   it the shared view model from `lib/personView`, so neither needs to know the
   other's field names, and the card cannot drift between them again.

   The section takes its values in two parts: `person` is what to show when not
   editing, `form` is what the inputs are bound to. They are the same shape, so
   a caller that edits in place can pass the same object twice. */

import { User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EditableField } from '@/components/admin/ProfileField';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  fixedRegionsForCountry,
  normalizeAddressCountryCode,
  regionDisplay,
} from '@/lib/addressRegions';
import { GENDER_OPTIONS, STATUS_IN_CANADA_OPTIONS, formatDate, labelFromOptions } from '@/lib/applications';
import {
  SelectControl,
  inputClass,
  sectionCardClass,
  sectionTitleClass,
  sectionTitleStyle,
} from './controls';

// Only these two have a fixed list of subdivisions worth offering; everywhere
// else the field is free text.
const PROVINCE_STATE_COUNTRIES = new Set(['CA', 'US']);

function countryName(countries, code) {
  const match = (countries || []).find((country) => country?.code === code);
  return match ? match.name : code || '';
}

export default function PersonalSection({
  person,
  form,
  editing = false,
  setField,
  setAddressField,
  countries = [],
  // The SIN is revealed through a different audited endpoint for an applicant
  // than for a student, so the page owns that and passes the result down.
  sinMask = '',
  revealedSin = '',
  onRevealSin,
  revealingSin = false,
  title = 'Personal Information',
}) {
  const view = person || {};
  const draft = form || view;
  const address = view.address || {};
  const draftAddress = draft.address || {};

  const countryCode = normalizeAddressCountryCode(draftAddress.country || address.country);
  const knownCountry = (countries || []).some((country) => country?.code === countryCode);
  const provinces = fixedRegionsForCountry(countryCode);
  const currentProvince = String(draftAddress.provinceState || '').trim();
  const currentProvinceName = regionDisplay(countryCode, currentProvince);
  // A province saved before this country had a fixed list still has to be
  // selectable, or opening the editor would silently drop it.
  const hasLegacyProvince = currentProvinceName
    && !provinces.some((province) => province.name === currentProvinceName);

  const set = (key) => (value) => setField?.(key, value);
  const setText = (key) => (event) => setField?.(key, event.target.value);
  const setAddressText = (key) => (event) => setAddressField?.(key, event.target.value);

  return (
    <Card className={sectionCardClass}>
      <CardHeader className="pb-3">
        <CardTitle className={sectionTitleClass} style={sectionTitleStyle}>
          <User className="w-4 h-4 text-[#D4AF37]" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <EditableField label="First name" editing={editing} display={view.firstName}>
          <Input value={draft.firstName || ''} onChange={setText('firstName')} className={inputClass} />
        </EditableField>
        <EditableField label="Last name" editing={editing} display={view.lastName}>
          <Input value={draft.lastName || ''} onChange={setText('lastName')} className={inputClass} />
        </EditableField>
        <EditableField label="Preferred name" editing={editing} display={view.preferredName}>
          <Input value={draft.preferredName || ''} onChange={setText('preferredName')} className={inputClass} />
        </EditableField>
        <EditableField label="Date of birth" editing={editing} display={formatDate(view.dateOfBirth)}>
          <Input type="date" value={draft.dateOfBirth || ''} onChange={setText('dateOfBirth')} className={inputClass} />
        </EditableField>
        <EditableField label="Gender" editing={editing} display={labelFromOptions(GENDER_OPTIONS, view.gender)}>
          <SelectControl value={draft.gender} onChange={set('gender')} options={GENDER_OPTIONS} />
        </EditableField>
        <EditableField
          label="Citizenship"
          editing={editing}
          display={countryName(countries, view.citizenshipCountry) || view.citizenshipCountry}
        >
          <Input value={draft.citizenshipCountry || ''} onChange={setText('citizenshipCountry')} className={inputClass} />
        </EditableField>
        <EditableField
          label="Phone"
          editing={editing}
          display={`${view.phoneCountryCode || ''} ${view.phoneNumber || ''}`.trim()}
        >
          <div className="flex gap-2">
            <Input
              value={draft.phoneCountryCode || ''}
              onChange={setText('phoneCountryCode')}
              className={`${inputClass} w-24`}
              placeholder="+1"
            />
            <Input
              value={draft.phoneNumber || ''}
              onChange={setText('phoneNumber')}
              className={inputClass}
              placeholder="Phone number"
            />
          </div>
        </EditableField>
        <EditableField label="Email" editing={editing} display={view.email}>
          <Input value={draft.email || ''} onChange={setText('email')} className={inputClass} />
        </EditableField>
        <EditableField
          label="Status in Canada"
          editing={editing}
          display={labelFromOptions(STATUS_IN_CANADA_OPTIONS, view.statusInCanada)}
        >
          <SelectControl
            value={draft.statusInCanada}
            onChange={set('statusInCanada')}
            options={STATUS_IN_CANADA_OPTIONS}
          />
        </EditableField>
        <EditableField label="Status expiry" editing={editing} display={formatDate(view.statusExpiryDate)}>
          <Input type="date" value={draft.statusExpiryDate || ''} onChange={setText('statusExpiryDate')} className={inputClass} />
        </EditableField>

        {/* Stored encrypted and never sent with the record. Revealing it is a
            separate audited read, so it has no editing counterpart here. */}
        {!editing && (
          <ProfileFieldSlot label="SIN" className="space-y-1.5">
            <ProfileFieldLabel label="SIN" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45" />
            <p className="text-sm text-[#062B1A] font-medium break-words flex flex-wrap items-center gap-2">
              <span>{revealedSin || sinMask || '—'}</span>
              {sinMask && !revealedSin && onRevealSin && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onRevealSin}
                  disabled={revealingSin}
                  className="h-7 border-[rgba(6,43,26,0.16)] px-2 text-xs text-[#062B1A]"
                >
                  {revealingSin ? 'Revealing...' : 'Reveal'}
                </Button>
              )}
            </p>
          </ProfileFieldSlot>
        )}

        <EditableField label="Apt/Unit/Suite #" editing={editing} display={address.unit}>
          <Input value={draftAddress.unit || ''} onChange={setAddressText('unit')} className={inputClass} />
        </EditableField>
        <EditableField label="Street Address" editing={editing} display={address.street}>
          <Input value={draftAddress.street || ''} onChange={setAddressText('street')} className={inputClass} />
        </EditableField>
        <EditableField label="City" editing={editing} display={address.city}>
          <Input value={draftAddress.city || ''} onChange={setAddressText('city')} className={inputClass} />
        </EditableField>
        <EditableField
          label="Country"
          editing={editing}
          display={countryName(countries, address.country) || address.country}
        >
          <Select value={countryCode || undefined} onValueChange={(value) => setAddressField?.('country', value)}>
            <SelectTrigger className={inputClass}>
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {countryCode && !knownCountry && (
                <SelectItem value={countryCode}>{draftAddress.country}</SelectItem>
              )}
              {(countries || []).filter((country) => country?.code).map((country) => (
                <SelectItem key={country.code} value={country.code}>{country.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </EditableField>
        <EditableField
          label="Province/State"
          editing={editing}
          display={regionDisplay(address.country, address.provinceState)}
        >
          {PROVINCE_STATE_COUNTRIES.has(countryCode) ? (
            <Select
              value={currentProvinceName || undefined}
              onValueChange={(value) => setAddressField?.('provinceState', value)}
            >
              <SelectTrigger className={inputClass}>
                <SelectValue placeholder="Select province/state" />
              </SelectTrigger>
              <SelectContent>
                {hasLegacyProvince && (
                  <SelectItem value={currentProvinceName}>{currentProvinceName} (existing)</SelectItem>
                )}
                {provinces.map((province) => (
                  <SelectItem key={province.code} value={province.code}>
                    {province.code} — {province.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={draftAddress.provinceState || ''}
              onChange={setAddressText('provinceState')}
              className={inputClass}
            />
          )}
        </EditableField>
        <EditableField label="Postal Code" editing={editing} display={address.postalCode}>
          <Input value={draftAddress.postalCode || ''} onChange={setAddressText('postalCode')} className={inputClass} />
        </EditableField>

        {/* Records written before the address was broken into columns carry one
            formatted string, and it is all they have. */}
        {!editing && view.addressLegacy && (
          <EditableField label="Address" editing={false} display={view.addressLegacy} full />
        )}
      </CardContent>
    </Card>
  );
}
